import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Building, Edit } from 'lucide-react';
import { EntityEditModal } from './EntityEditModal';
import { InvestorEntity } from '../services/investor-service';
import { getEntityTypeColor } from '../utils/entity-formatters';
import { BankDetails } from './BankDetails';
import { SecondaryContactsList } from './SecondaryContactsList';
import { EntityTopRow } from './EntityTopRow';

interface InvestorEntitiesViewProps {
  entities: InvestorEntity[];
}

export function InvestorEntitiesView({ entities }: InvestorEntitiesViewProps) {
  const [editingEntity, setEditingEntity] = useState<InvestorEntity | null>(
    null
  );
  const [showEditModal, setShowEditModal] = useState(false);

  const handleEditEntity = (entity: InvestorEntity) => {
    setEditingEntity(entity);
    setShowEditModal(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Investing Entities</h2>
      </div>

      {entities.map((entity) => (
        <Card key={entity.id}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Building className="h-5 w-5 flex-shrink-0" />
                <div className="flex items-center gap-2 flex-wrap">
                  <CardTitle className="m-0">{entity.name}</CardTitle>
                  <Badge className={getEntityTypeColor(entity.type)}>
                    {entity.type}
                  </Badge>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleEditEntity(entity)}
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Top row - Address, Contacts, Entity Details */}
              <EntityTopRow entity={entity} />
              <SecondaryContactsList contacts={entity.contacts.secondary} />

              {/* Bank Details */}
              {entity && entity.bankDetails && (
                <BankDetails bankDetails={entity.bankDetails} />
              )}
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Edit Modal */}
      <EntityEditModal
        open={showEditModal}
        onOpenChange={setShowEditModal}
        entity={editingEntity}
      />
    </div>
  );
}
